import type { ContextType, ReactNode } from "react";
import { Children, cloneElement, Component } from "react";

import styled from "styled-components";

import { Dropdown, Bubble } from "@similarweb/ui-components";
import type { Translate } from "@similarweb/i18n-provider";

import { PreferencesService } from "app/services/preferences/preferencesService";

import { TranslationContext } from "../../WithTranslation/src/TranslationProvider";

import { GroupCreationDropdownItem } from "./GroupCreationDropdownItem";

interface IKeywordGroupCreationDropdownProps {
    children: ReactNode;
    onKeywordsModalClick: () => void;
    onGeneratorToolClick: () => void;
    buttonWidth?: string;
    containerClass?: string;
    bubbleClass?: string;
    bubbleDirection?: "right" | "left";
    dropdownPopupPlacement?: string;
    onDropDownToggle?: (isOpen: boolean) => void;
    hideNotification?: boolean;
    appendTo?: string;
    hasKeywordsGenerator: boolean;
}

interface IKeywordGroupCreationDropdownState {
    showNotification: boolean;
}

const USER_PREFERENCES_KEY = "keywordsGeneratorPromotion_Beta5";

const ToolTitle = styled.span`
    display: flex;
    align-items: center;
`;

export class KeywordGroupCreationDropdown extends Component<
    IKeywordGroupCreationDropdownProps,
    IKeywordGroupCreationDropdownState
> {
    static contextType = TranslationContext;

    context: ContextType<typeof TranslationContext>;

    private static defaultProps = {
        onDropDownToggle: (isOpen: boolean) => null,
        dropdownPopupPlacement: "bottom-right",
        hasKeywordsGenerator: false,
    };

    constructor(props: IKeywordGroupCreationDropdownProps) {
        super(props);
        this.state = {
            showNotification: !PreferencesService.get(USER_PREFERENCES_KEY),
        };
    }

    public removeBubble = async (isOpen?: FixmeType) => {
        await PreferencesService.add({ [USER_PREFERENCES_KEY]: true });
        this.setState({
            showNotification: false,
        });
        // call this method only only if isOpen is true or false
        if (isOpen !== "undefined") {
            this.props.onDropDownToggle?.(isOpen);
        }
    };

    public render() {
        const {
            children,
            onKeywordsModalClick,
            onGeneratorToolClick,
            buttonWidth,
            containerClass,
            bubbleClass,
            bubbleDirection,
            dropdownPopupPlacement,
        } = this.props;
        // @ts-ignore FIXME: strict migration - Property 'translate' does not exist on type 'TranslateContextValue | undefined' — TranslateContext type from @similarweb/i18n-provider is external, provider guaranteed at runtime
        const { translate } = this.context;

        const cssClassContainer = `DropdownContent-container Popup-content--pro-dropdown ${containerClass}`;
        const augment = (child: FixmeType) => cloneElement(child, { onClick: onKeywordsModalClick });

        // validate that children is a component and not an array (see use in Dropdown's definition)
        // @ts-ignore FIXME: strict migration - 'children' is possibly 'null' or 'undefined' — children is a required prop, guaranteed defined at runtime
        const child = children.constructor === Array ? children[0] : children;

        if (!this.props.hasKeywordsGenerator) {
            return Children.map(children, augment);
        }

        return (
            <Bubble
                isOpen={!this.props.hideNotification ? this.state.showNotification : false}
                onClose={this.removeBubble}
                placement={`bottom-${bubbleDirection}`}
                title={translate("keywords.generator.promotion.bubble.title")}
                text={translate("keywords.generator.promotion.bubble.text")}
                cssClass={`Bubble-element ${bubbleClass}`}
            >
                <div>
                    <Dropdown
                        width={328}
                        appendTo={this.props.appendTo}
                        onToggle={this.removeBubble}
                        cssClassContainer={cssClassContainer}
                        dropdownPopupPlacement={dropdownPopupPlacement}
                        buttonWidth={buttonWidth}
                    >
                        {child}
                        {UseKeywordsModal(onKeywordsModalClick, translate)}
                        {UseGeneratorTool(onGeneratorToolClick, translate)}
                    </Dropdown>
                </div>
            </Bubble>
        );
    }
}

const UseKeywordsModal = (onClick: () => void, translate: Translate) => {
    return (
        <GroupCreationDropdownItem
            preventDefault={true}
            onClick={onClick}
            iconName="add"
            description={translate("keyword.generator.tool.add.group.dropdown.upload.description")}
            title={translate("keyword.generator.tool.add.group.dropdown.upload.title")}
        />
    );
};

const UseGeneratorTool = (onClick: () => void, translate: Translate) => {
    return (
        <GroupCreationDropdownItem
            preventDefault={true}
            onClick={onClick}
            iconName="wand"
            description={translate("keyword.generator.tool.add.group.dropdown.tool.description")}
            title={<ToolTitle>{translate("keyword.generator.tool.add.group.dropdown.tool.title")}</ToolTitle>}
        />
    );
};
